import isEmpty from 'lodash/isEmpty'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Book, Rates } from 'src/subscribe'
import { Context } from 'src/Subscribe_'
import AutoChargeTerms from './AutoChargeTerms'
import AWelcomeMessageFromYouToTheRecipient from './AWelcomeMessageFromYouToTheRecipient'
import DelaySendingWelcomeMessage from './DelaySendingWelcomeMessage'
import RecipientsFullNameAndEmailAddress from './RecipientsFullNameAndEmailAddress'
import ShippingAddressAndPhoneNumber from './ShippingAddressAndPhoneNumber'
import WhichBookShouldTheSubscriptionStartWith from './WhichBookShouldTheSubscriptionStartWith'
import InternationalPricingNote from './InternationalPricingNote'

export type FieldValues = {
  address: {
    address_line1: string
    address_line2: string
    administrative_area: string
    country_code: string
    locality: string
    organization: string
    postal_code: string
  }
  delay_welcome_message?: string
  delay_until?: string
  email?: string
  id: string
  message?: string
  name?: string
  phone: string
  starting_book_number?: string
}

const RecipientsInformation = ({
  autoChargeTerms,
  internationalPricingNote,
  rates,
  startableBooks,
}: {
  autoChargeTerms: string | null
  internationalPricingNote: string | null
  rates: Rates
  startableBooks: Book[]
}) => {
  const { dispatch, state } = useContext(Context)

  const id = useMemo(
    () =>
      state.recipientBeingEdited
        ? state.recipientBeingEdited.id
        : Math.random()
            .toString(36)
            .substring(2, 4 + 2),
    []
  )

  const isGift = useMemo(
    () => state.values.is_gift === '1',
    [state.values.is_gift]
  )

  let defaultValues: FieldValues = {
    address: {
      address_line1: '',
      address_line2: '',
      administrative_area: '',
      country_code: 'US',
      locality: '',
      organization: '',
      postal_code: '',
    },
    delay_welcome_message: '0',
    delay_until: '',
    email: '',
    id,
    message: '',
    name: '',
    phone: '',
    starting_book_number: startableBooks[0]!.number.toString(),
  }
  if (state.recipientBeingEdited) {
    const r = state.recipientBeingEdited
    defaultValues = {
      address: {
        address_line1: r.address?.address_line1 || '',
        address_line2: r.address?.address_line2 || '',
        administrative_area: r.address?.administrative_area || '',
        country_code: r.address?.country_code || 'US',
        locality: r.address?.locality || '',
        organization: r.address?.organization || '',
        postal_code: r.address?.postal_code || '',
      },
      delay_welcome_message: r.delay_welcome_message || '0',
      delay_until: r.delay_until || '',
      email: r.email || '',
      id,
      message: r.message || '',
      name: r.name || '',
      phone: r.phone || '',
      starting_book_number:
        r.starting_book_number || startableBooks[0]!.number.toString(),
    }
  }

  const {
    clearErrors,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setError,
    watch,
  } = useForm<FieldValues>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values) => {
      // If necesssary, reset delay_welcome_message and delay_until to values
      // that make sense
      const v = {
        ...values,
      }
      if (values.message === '') {
        v.delay_welcome_message = '0'
        v.delay_until = ''
      }
      if (v.delay_welcome_message === '0') {
        v.delay_until = ''
      }

      if (isGift) {
        dispatch({
          payload: {
            recipientBeingEdited: v,
          },
          type: 'review clicked',
        })
      } else {
        dispatch({
          payload: {
            recipientBeingEdited: v,
          },
          type: 'recipients information payment clicked',
        })
      }
    },
    [isGift]
  )

  useEffect(() => {
    const error = state.serverErrors.subscribers_already_exist?.[id]
    if (error) {
      setError('email', {
        message: error,
      })
    } else {
      clearErrors('email')
    }
  }, [state.serverErrors.subscribers_already_exist?.purchaser_email_address])

  const handleBackClick = useCallback(() => {
    dispatch({
      payload: {
        recipientBeingEdited: getValues(),
      },
      type: 'recipients information back clicked',
    })
  }, [])

  const handleCancelClick = useCallback(() => {
    dispatch({
      type: 'recipients information cancel clicked',
    })
  }, [])

  const showBackButton = useMemo(() => {
    if (!isGift) {
      return true
    }
    if (isEmpty(state.values.recipients)) {
      return true
    }
    return false
  }, [isGift, state.values.recipients])

  const showCancelButton = useMemo(() => {
    if (!isGift) {
      return false
    }
    if (isEmpty(state.values.recipients)) {
      return false
    }
    if (
      Object.values(state.values.recipients).length === 1 &&
      state.values.recipients[id]
    ) {
      return false
    }
    return true
  }, [id, isGift, state.values.recipients])

  const otherEmails: string[] = useMemo(() => {
    if (isEmpty(state.values.recipients)) {
      return []
    }
    const oes: string[] = []
    Object.values(state.values.recipients).forEach((r) => {
      if (r.email && r.id !== id) {
        oes.push(r.email)
      }
    })
    return oes
  }, [id])

  return (
    <form className="page page4 show" onSubmit={handleSubmit(onSubmit)}>
      {isGift && (
        <RecipientsFullNameAndEmailAddress
          errors={errors}
          otherEmails={otherEmails}
          register={register}
        />
      )}
      <ShippingAddressAndPhoneNumber
        errors={errors}
        isGift={isGift}
        register={register}
        watch={watch}
      />
      {startableBooks.length > 1 && (
        <WhichBookShouldTheSubscriptionStartWith
          register={register}
          startableBooks={startableBooks}
        />
      )}
      {isGift && (
        <AWelcomeMessageFromYouToTheRecipient
          errors={errors}
          register={register}
        />
      )}
      {isGift && watch('message') !== '' && (
        <DelaySendingWelcomeMessage
          errors={errors}
          register={register}
          watch={watch}
        />
      )}
      {internationalPricingNote && (
        <InternationalPricingNote
          note={internationalPricingNote}
          rates={rates}
          watch={watch}
        />
      )}
      {!isGift && (
        <AutoChargeTerms rates={rates} terms={autoChargeTerms} watch={watch} />
      )}
      <div
        className="navigation js-form-wrapper form-wrapper"
        id="edit-navigation--4"
      >
        <button
          className="continue show"
          disabled={state.isSubmitting}
          type="submit"
        >
          {!isGift && <span>Payment</span>}
          {isGift && <span>Review</span>}
        </button>
        {state.serverErrors.nonce && (
          <div className="server-error-message">
            Server error: {state.serverErrors.nonce}
          </div>
        )}
        {state.serverErrors.startable_books && (
          <div className="server-error-message">
            Server error: {state.serverErrors.startable_books}
          </div>
        )}
        {showBackButton && (
          <button
            className="back show"
            disabled={state.isSubmitting}
            onClick={handleBackClick}
            type="button"
          >
            Back
          </button>
        )}
        {showCancelButton && (
          <button
            className="back show"
            disabled={state.isSubmitting}
            onClick={handleCancelClick}
            type="button"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}

export default RecipientsInformation
